// src/components/common/FilterPillRow.jsx
/**
 * FilterPillRow — horizontally scrollable glass pill filter row.
 *
 * @description
 *   Tier 2 (glass pill) family for filter/segment selectors. Default mobile-only
 *   render (xs breakpoint) keeps it from forking the desktop filter bar; the
 *   `alwaysShow` flag lets a caller mirror the same component family on
 *   desktop so list views with 5+ filter dimensions stay visually consistent
 *   across breakpoints (RMA Overview pattern, 2026-05-10).
 *
 *   Long-tail filter dimensions (Status, Case type, hide-toggles) belong
 *   behind `moreFiltersContent` — opens a SwipeableDrawer with the rendered
 *   sheet body. The "Filters" tune-pill at the START of the row is the entry
 *   point (T3-12: until 2026-10-08 it sat behind every option pill with
 *   `scrollSnapAlign: 'end'`, so on a phone it was the one control a reader had
 *   to scroll to find). Avoids dual-family layouts (BottomSheetSelect glass-pill
 *   + MUI Chip pill) which break visual rhythm.
 *
 * @useWhen
 *   - List views with 5+ filter dimensions where the long-tail belongs in a sheet
 *   - Mobile-first dashboards where filter bar must scroll horizontally
 *   - When you want one component family for inline + sheet filters
 *   - RMA Overview / PO Inbox / Vendor Reports list — list views needing unified pill rhythm
 *
 * @dontUseFor
 *   - Single-select dropdowns — use BottomSheetSelect
 *   - 2 or fewer dimensions where a plain inline Select fits
 *   - Active-filter "what's selected" strip — that's a separate
 *     visual surface (caller's responsibility); FilterPillRow is the
 *     toggle source, not the receipt of selections
 *
 * @related
 *   - BottomSheetSelect / BottomSheetMultiSelect — single dimension, used INSIDE moreFiltersContent
 *   - Chip — generic primitive; FilterPillRow wraps the same role w/ glass-pill styling
 *
 * @reuseHints
 *   - Vendor list tier filters / order-status quickbar / market-lab cohort selectors
 *
 * Props:
 *   options             : [{ value, label, count?, ariaLabel?, title? }]
 *                                                      — filter options.
 *                                                         🔴 `ariaLabel`/`title`
 *                                                         (2026-10-13, rein
 *                                                         additiv): Langform
 *                                                         für eine Pille, deren
 *                                                         sichtbarer Text zu
 *                                                         kurz für ihre
 *                                                         Bedingung ist. Ohne
 *                                                         sie ist der Baum
 *                                                         unverändert
 *                                                         (`aria-label` bleibt
 *                                                         `label`).
 *                                                         ⚠️ `ariaLabel` MUSS
 *                                                         den sichtbaren Text
 *                                                         voranstellen
 *                                                         (WCAG 2.5.3 „Label in
 *                                                         Name").
 *                                                         🔴 `count` (G5a-
 *                                                         Nachbesserung 5,
 *                                                         2026-10-14, R16 W2):
 *                                                         steht die Zahl im
 *                                                         Knopf, steht sie auch
 *                                                         im zugaenglichen
 *                                                         Namen — `aria-label`
 *                                                         ist „Unread 54", nicht
 *                                                         „Unread". Sonst fehlt
 *                                                         sie Screenreadern
 *                                                         ganz (WCAG 1.3.1 +
 *                                                         2.5.3). Ohne `count`
 *                                                         bleibt der Name
 *                                                         unveraendert.
 *   value               : string | string[]            — selected value(s)
 *   onChange            : (value) => void              — called with clicked value
 *   multi               : bool                         — allow multi-select (default false)
 *   onSearch            : () => void                   — if set, renders search T3 icon btn
 *   onMoreFilters       : () => void                   — if set, renders '···' pill at the start (custom handler)
 *   moreFiltersContent  : ReactNode | fn(close)        — if set, renders '···' pill and opens
 *                                                         built-in bottom sheet with this content
 *   moreFiltersTitle    : string                       — title for the built-in bottom sheet
 *   alwaysShow          : bool                         — render even on desktop (default false)
 *   label               : string                       — dimension name of a VALUE ROW ("Status"),
 *                                                         drawn as "Status:" before the pills and
 *                                                         used as the group's accessible name
 *   semantics           : 'and' | 'or'                 — how the pills combine, at the group's
 *                                                         accessible name. Read only when `label`
 *                                                         is set; omitted, a `label` row is 'or'
 *                                                         (a value row is or-linked, N3.3). An
 *                                                         explicit value always wins
 *   disabled            : bool                         — 🔴 optional (G0.12, UI2 Teil 10,
 *                                                         rein additiv, Vorgabe false):
 *                                                         sperrt jede Optionspille, den
 *                                                         Suchknopf und den „Filters"-Öffner
 *                                                         (N8.5 „keine Eingabe ohne Folge").
 *                                                         🔴 **Und gesperrt SIEHT gesperrt
 *                                                         aus** (R16 W2, Nachbesserung 2,
 *                                                         2026-10-13): die drei sind nackte
 *                                                         `Box component="button"` und tragen
 *                                                         gesperrt dieselbe Deckkraft wie die
 *                                                         Hausform (`BottomSheetSelect`,
 *                                                         `opacity: disabled ? 0.5 : 1`), dazu
 *                                                         `cursor: default`, kein Schatten und
 *                                                         kein Druckausschlag. Ohne die Prop
 *                                                         ist der Baum unverändert.
 *                                                         Den GRUND nennt der Aufrufer
 *                                                         sichtbar direkt über der Leiste — `DisabledReason`
 *                                                         scheidet aus (`DisabledReason.jsx`
 *                                                         `@dontUseFor`: es kann eine
 *                                                         durchgereichte Ursache nicht benennen).
 *   sx                  : object                       — extra sx on container
 *
 * 🔴 Zwei Reihenarten in EINER Komponente (Konzept §3.2, UI2 Teil 10 G0.6):
 *   ohne `label`  = Quick-Filter-Reihe. Mehrere benannte Bedingungen,
 *                   und-verknuepft (N3.3), keine gedrueckte Pille = keine
 *                   Einschraenkung. Das ist die heutige Form.
 *   mit `label`   = Werte-Pillenreihe EINER Primaerfacette. Die Reihe beginnt
 *                   mit dem Dimensionsnamen, die Werte sind oder-verknuepft
 *                   (N3.3) — deshalb ist `semantics` ohne Angabe hier 'or' und
 *                   nicht 'and'. 🔴 `label` ist rein additiv: ohne die Prop gibt
 *                   es weder das sichtbare Wort noch `role="group"` — der Baum
 *                   der sieben heutigen Aufrufer ist Zeichen fuer Zeichen der
 *                   alte. Das ist Absicht: ein unbedingt gesetztes `role`
 *                   waere eine Aenderung an jeder bestehenden Ansicht.
 *
 * 🔴 Der Kasten mit `role="group"`/`aria-label` traegt NUR die Optionspillen —
 *   der Dimensionsname steht als Geschwister DAVOR, Suchknopf und „Filters"-
 *   Oeffner liegen AUSSERHALB (R16 W1). Sonst hoerte ein Screenreader „Status
 *   (any of), Gruppe … Status: Suchen Filter" — und der Name der Gruppe
 *   verspraeche etwas, das nicht in ihr liegt.
 *
 * 🔴 `data-testid="filter-pill"` (K-T1-Nachbesserung 2, 2026-10-09) sitzt an
 *   jeder OPTIONSPILLE — nicht am Such- und nicht am „Filters"-Knopf. Es ist die
 *   benannte Messflaeche fuer `tests/design/ui2-k-t1-bewegung.spec.ts`
 *   (Druckausschlag, `GLASS_PILL_SX`), kein Gestaltungsmerkmal: die Kennung
 *   aendert am Rendern nichts.
 */
import React, { useState } from 'react';
import {
  Box, Typography, useTheme, useMediaQuery, SwipeableDrawer, IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import CloseIcon from '@mui/icons-material/Close';
import { GLASS_PILL_SX, ROUND_ICON_BTN_SX, TOUCH_TARGET_COARSE } from '../../theme/tokens';
import { BOTTOM_SHEET_TRANSITION } from '../../theme/tokens';

export default function FilterPillRow({
  options = [],
  value,
  onChange,
  multi = false,
  onSearch,
  onMoreFilters,
  moreFiltersContent,
  moreFiltersTitle = 'Filters',
  alwaysShow = false,
  label,
  semantics,
  disabled = false,
  sx = {},
}) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const closeSheet = () => setSheetOpen(false);
  const openSheet = () => setSheetOpen(true);

  // If moreFiltersContent is supplied, prefer the built-in sheet; otherwise
  // fall back to the caller's onMoreFilters handler.
  const moreFiltersHandler = moreFiltersContent
    ? openSheet
    : onMoreFilters;
  const hasMoreFilters = !!moreFiltersHandler;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!alwaysShow && !isMobile) return null;

  const selected = multi
    ? (Array.isArray(value) ? value : [value].filter(Boolean))
    : value;

  const isActive = (v) =>
    multi ? selected.includes(v) : selected === v;

  const handleClick = (v) => {
    // 🔴 G0.12 (N8.5): gesperrt wählt nichts aus. Ein `disabled` an der Pille
    // unterbindet das Klickereignis schon im Browser; der Riegel hier haelt auch
    // den programmatischen Weg still (Hausform `SearchTrigger.handleInput`).
    if (disabled) return;
    if (!multi) {
      onChange(v);
      return;
    }
    if (selected.includes(v)) {
      onChange(selected.filter((s) => s !== v));
    } else {
      onChange([...selected, v]);
    }
  };

  const handleKeyDown = (fn) => (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fn();
    }
  };

  // 🔴 Der Gruppenname entsteht NUR mit `label`. Ohne ihn bleibt der Kasten
  // namenlos — genau wie vor dieser Aenderung. Die Semantik steht im Namen,
  // nicht in einem zweiten Attribut: `and`/`or` beschreibt, wie die Pillen
  // sich verhalten (N3.3), und ein Screenreader hoert das nur, wenn es im
  // zugänglichen Namen steht.
  // 🔴 Vorgabe ist 'or': eine Werte-Reihe ist oder-verknuepft (N3.3), und `and`
  // waere genau der Wert, der in dieser Betriebsart nicht vorkommen darf. Ohne
  // `label` wird `semantics` gar nicht gelesen (der Name entsteht nur mit
  // `label`); ein ausdruecklich uebergebenes `semantics` gewinnt.
  const semantik = semantics ?? 'or';
  const gruppenName = label
    ? `${label} (${semantik === 'or' ? 'any of' : 'all of'})`
    : undefined;

  // Die Optionspillen — MIT `label` in einen eigenen Kasten gelegt, der NUR sie
  // traegt (unten). Ein Fragment aendert nichts am DOM: es rendert seine Kinder
  // unmittelbar dort, wo es steht.
  const pillen = (
    <>
      {options.map((opt) => {
        const active = isActive(opt.value);
        // 🔴 R16 W2 (G5a-Nachbesserung 5, 2026-10-14): die ZAHL gehört in den
        // zugänglichen Namen, wenn sie im Knopf steht.
        //
        // `aria-label` ERSETZT den Inhalt vollständig — ohne diese Zeile hieß
        // die Pille „Unread", obwohl „54" sichtbarer Text DESSELBEN Knopfes ist
        // (ein eigener Knoten darunter, `:287-304`). Damit fehlt die Zahl
        // Screenreadern ganz: WCAG 1.3.1 (Information nur visuell) und
        // WCAG 2.5.3 „Label in Name" (Sichttext „Unread 54", Name „Unread").
        // 🔴 Die Hausform für den Geschwisterknopf ist dieselbe Prüfung und
        // steht seit G0.5 im Baustein (`FilterSheet.jsx:285` „Filters, N
        // active", Konzept N4.6) — für die Pillen fehlte sie.
        //
        // 🔴 Rein additiv, und die Trennung ist die Trennschärfe: OHNE `count`
        // ist der Name Zeichen für Zeichen der alte (`opt.ariaLabel ||
        // opt.label`) — die sieben Bestandsaufrufer und die Musterroute
        // rendern unverändert. Mit `count` trägt er die Zahl HINTEN, damit
        // `ariaLabel` weiter den sichtbaren Text VORANstellt (die Zusage aus
        // dem Kasten an `:227`); der Name bleibt ein Oberstring des
        // Sichttextes.
        const name = opt.ariaLabel || opt.label;
        const pilleName = opt.count != null ? `${name} ${opt.count}` : name;
        return (
          <Box
            key={opt.value}
            component="button"
            onClick={() => handleClick(opt.value)}
            onKeyDown={handleKeyDown(() => handleClick(opt.value))}
            aria-pressed={active}
            // 🔴 `ariaLabel`/`title` sind rein additiv (2026-10-13): eine Pille,
            // deren sichtbarer Text zu kurz fuer ihre Bedingung ist („In stock,
            // hidden"), nennt die Langform im Tooltip UND im zugaenglichen
            // Namen. Ohne die Felder ist der Baum Zeichen fuer Zeichen der alte
            // (`title={undefined}` laesst React das Attribut aus), und der
            // zugaengliche Name bleibt `opt.label`.
            // ⚠️ `ariaLabel` MUSS den sichtbaren Text voranstellen — sonst
            // bricht WCAG 2.5.3 „Label in Name". Mit gesetztem `count` hängt
            // die Zahl HINTEN an (`pilleName`, s. o.).
            aria-label={pilleName}
            title={opt.title}
            // 🔴 G0.12 (N8.5, rein additiv): gesperrt traegt JEDE Optionspille
            // `disabled` (der echte Knopf-Zustand) UND `aria-disabled` (der
            // ausdrueckliche Vermerk, den die Probe liest). Ohne die Prop sind
            // beide `false`/`undefined` und React laesst sie aus.
            disabled={disabled || undefined}
            aria-disabled={disabled || undefined}
            // 🔴 Die Kennung BENENNT die Messflaeche (K-T1-Nachbesserung 2,
            // 2026-10-09). `ui2-k-t1-bewegung.spec.ts` misst den Druckausschlag
            // an dieser Pille; bis dahin suchte es `button[aria-pressed]` per
            // Position und traf den „Customize"-Knopf der Seitenleiste (MUI-
            // `Button`, 6 px). `aria-pressed` benennt keine Flaeche — es traegt
            // auch ein `ToggleButton` oder eine `Pill` mit `selected`, und
            // beide fuehren `GLASS_PILL_SX` NICHT.
            data-testid="filter-pill"
            sx={{
              ...GLASS_PILL_SX,
              scrollSnapAlign: 'start',
              gap: 0.5,
              bgcolor: active ? 'primary.main' : 'var(--glass-bg)',
              color: active ? 'primary.contrastText' : 'text.primary',
              fontWeight: active ? 600 : 500,
              // active pill: no blur needed — solid fill
              backdropFilter: active ? 'none' : GLASS_PILL_SX.backdropFilter,
              WebkitBackdropFilter: active ? 'none' : GLASS_PILL_SX.WebkitBackdropFilter,
              border: active ? '1px solid transparent' : GLASS_PILL_SX.border,
              boxShadow: active || disabled ? 'none' : GLASS_PILL_SX.boxShadow,
              // 🔴 G0.12 (N8.5 / R16 W2, Nachbesserung 2, 2026-10-13): gesperrt
              // SIEHT gesperrt aus — mit DERSELBEN Deckkraft-Dämpfung wie die
              // Facettenpille der Hausform (`BottomSheetSelect.jsx:685`,
              // `opacity: disabled ? 0.5 : 1`). Literal, kein gemeinsamer Token:
              // ein solcher müsste `BottomSheetSelect` mitanfassen, das hier
              // unverändert bleibt. Nur im `disabled`-Zweig (der leere Spread
              // fällt ohne die Prop weg), damit der Baum ohne sie Zeichen für
              // Zeichen der alte ist — die frühere Farb-/Hintergrund-Dämpfung
              // über `action.disabled`/`action.disabledBackground` entfällt.
              ...(disabled ? { opacity: 0.5 } : {}),
              cursor: disabled ? 'default' : 'pointer',
              // 🔴 Kein Druckausschlag an der toten Pille: `GLASS_PILL_SX` trägt
              // den Druck unter grobem Zeiger (`TOUCH_TARGET_COARSE_ACTIVE`). Der
              // zweite `'@media (pointer: coarse)'`-Schlüssel ERSETZT den ersten —
              // der 44-px-Boden bleibt, der Ausschlag fällt weg.
              ...(disabled
                ? {
                  '@media (pointer: coarse)': {
                    ...TOUCH_TARGET_COARSE['@media (pointer: coarse)'],
                    '&:active': { transform: 'none' },
                  },
                }
                : {}),
              // reset native button styles
              fontFamily: 'inherit',
            }}
          >
            <Typography sx={{ fontSize: 12, fontWeight: 'inherit', lineHeight: 1 }}>
              {opt.label}
            </Typography>
            {opt.count != null && (
              <Box
                sx={{
                  minWidth: 18,
                  height: 18,
                  borderRadius: 99,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: active ? 'rgba(255,255,255,0.25)' : 'action.selected',
                  px: 0.5,
                }}
              >
                <Typography sx={{ fontSize: 10, fontWeight: 700, lineHeight: 1, color: 'inherit' }}>
                  {opt.count}
                </Typography>
              </Box>
            )}
          </Box>
        );
      })}
    </>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        overflowX: 'auto',
        scrollSnapType: 'x mandatory',
        // hide scrollbar visually but keep functional
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        py: 0.5,
        ...sx,
      }}
    >
      {/* Werte-Reihe (Konzept §3.2): der Dimensionsname steht VOR den Werten —
          „Status: Open Waiting Draft". Er ist Text, keine Pille: er waehlt
          nichts aus und darf nicht wie ein Bedienelement aussehen (N3.8). */}
      {label && (
        <Typography
          component="span"
          variant="caption"
          color="text.secondary"
          sx={{
            flexShrink: 0,
            scrollSnapAlign: 'start',
            fontWeight: 600,
            lineHeight: 1,
          }}
        >
          {label}:
        </Typography>
      )}

      {/* Search trigger (T3 round icon button) */}
      {onSearch && (
        <Box
          component="button"
          onClick={onSearch}
          onKeyDown={handleKeyDown(onSearch)}
          aria-label="Search"
          // 🔴 G0.12 (N8.5): der Suchknopf sperrt mit (N8.5 nennt „Suche" neben
          // Facetten und Quick-Filtern). Additiv — ohne die Prop kein Attribut.
          disabled={disabled || undefined}
          aria-disabled={disabled || undefined}
          sx={{
            ...ROUND_ICON_BTN_SX,
            flexShrink: 0,
            scrollSnapAlign: 'start',
            // 🔴 G0.12 (N8.5 / R16 W2, Nachbesserung 2, 2026-10-13): der Suchknopf
            // ist ein nackter `Box component="button"` — die MUI-Dämpfung greift
            // hier nicht. Gesperrt dieselbe Deckkraft wie die Optionspille und die
            // Hausform (`BottomSheetSelect.jsx:685`), dazu keine Zeiger-Hand,
            // kein Schatten, kein Druckausschlag; die Farb-Dämpfung entfällt.
            color: 'text.secondary',
            ...(disabled ? { opacity: 0.5 } : {}),
            boxShadow: disabled ? 'none' : ROUND_ICON_BTN_SX.boxShadow,
            ...(disabled
              ? { '@media (pointer: coarse)': { '&:active': { transform: 'none' } } }
              : {}),
            cursor: disabled ? 'default' : 'pointer',
            border: 'none',
            background: 'none',
            p: 0,
          }}
        >
          <SearchIcon sx={{ fontSize: 18 }} />
        </Box>
      )}

      {/* 🔴 T3-12 (UI-Durchgang 2, Teil 3): der Öffner steht am ANFANG der
          Reihe, nicht am Ende. Vorher trug er `scrollSnapAlign: 'end'` und lag
          hinter allen Optionspillen — auf einem Telefon mit fünf Dimensionen
          also außerhalb des Sichtbereichs, und die Fläche, die die
          Langschwanz-Filter trägt, war genau die, die man erst suchen musste.
          Die Suche bleibt davor (sie ist keine Pille dieser Familie). */}
      {hasMoreFilters && (
        <Box
          component="button"
          onClick={moreFiltersHandler}
          onKeyDown={handleKeyDown(moreFiltersHandler)}
          aria-label="More filters"
          // 🔴 G0.12 (N8.5): der „Filters"-Öffner sperrt mit.
          disabled={disabled || undefined}
          aria-disabled={disabled || undefined}
          sx={{
            ...GLASS_PILL_SX,
            flexShrink: 0,
            scrollSnapAlign: 'start',
            gap: 0.5,
            // 🔴 G0.12 (N8.5 / R16 W2, Nachbesserung 2, 2026-10-13): der
            // „Filters"-Öffner ist ebenfalls ein nackter `Box component="button"`.
            // Gesperrt dieselbe Deckkraft wie die Optionspille und die Hausform
            // (`BottomSheetSelect.jsx:685`) — keine Zeiger-Hand, kein Schatten,
            // kein Druckausschlag; die Farb-Dämpfung entfällt.
            bgcolor: 'var(--glass-bg)',
            color: 'text.secondary',
            px: 1.5,
            ...(disabled ? { opacity: 0.5 } : {}),
            cursor: disabled ? 'default' : 'pointer',
            border: GLASS_PILL_SX.border,
            boxShadow: disabled ? 'none' : GLASS_PILL_SX.boxShadow,
            ...(disabled
              ? {
                '@media (pointer: coarse)': {
                  ...TOUCH_TARGET_COARSE['@media (pointer: coarse)'],
                  '&:active': { transform: 'none' },
                },
              }
              : {}),
            fontFamily: 'inherit',
          }}
        >
          <TuneIcon sx={{ fontSize: 15 }} />
          <Typography sx={{ fontSize: 12, fontWeight: 500, lineHeight: 1 }}>
            Filters
          </Typography>
        </Box>
      )}

      {/* 🔴 Mit `label` wandern die Optionspillen in einen eigenen Kasten, der
          NUR sie traegt: `role="group"`/`aria-label` gehoeren um die WERTE,
          nicht um Suchknopf und „Filters"-Oeffner (R16 W1). Ohne `label` gibt
          es diesen Kasten NICHT — `pillen` haengt dann unmittelbar in der
          Reihe, und der Baum der sieben Aufrufer ist Zeichen fuer Zeichen der
          alte. Visuell identisch: gleiche `gap`, gleiche Ausrichtung; beide
          sind Flex-Kinder derselben Reihe. */}
      {gruppenName ? (
        <Box
          role="group"
          aria-label={gruppenName}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            flexShrink: 0,
          }}
        >
          {pillen}
        </Box>
      ) : (
        pillen
      )}

      {/* Built-in bottom sheet (CR-020 standard pattern) */}
      {moreFiltersContent && (
        <SwipeableDrawer
          anchor="bottom"
          open={sheetOpen}
          onClose={closeSheet}
          onOpen={() => {}}
          disableSwipeToOpen
          PaperProps={{ sx: { borderRadius: '16px 16px 0 0', maxHeight: '85vh' } }}
          {...BOTTOM_SHEET_TRANSITION}
        >
          {/* Drag handle */}
          <Box sx={{ width: 36, height: 4, borderRadius: 99, bgcolor: 'divider', mx: 'auto', mt: 1.5 }} />
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            px: 2, pt: 1.5, pb: 1,
          }}>
            <Typography variant="subtitle1" fontWeight={600}>{moreFiltersTitle}</Typography>
            <IconButton
              onClick={closeSheet}
              aria-label="Close filters"
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{
            px: 2, pb: 'calc(env(safe-area-inset-bottom) + 16px)',
            overflowY: 'auto',
          }}>
            {typeof moreFiltersContent === 'function'
              ? moreFiltersContent(closeSheet)
              : moreFiltersContent}
          </Box>
        </SwipeableDrawer>
      )}
    </Box>
  );
}
